import { useCallback, useState } from 'react'
import { SortDirection, SortField } from '../utils/sortUtils'
import { SORT_DELAYS } from '../utils/reportUtils'

/**
 * Custom hook to manage sorting state and operations
 * 
 * @param initialField Initial field to sort by
 * @param initialDirection Initial sort direction
 * @returns Sort state and handlers
 */
const useSortableTable = (
  initialField: SortField = 'name',
  initialDirection: SortDirection = 'asc'
) => {
  // State for tracking sort field and direction
  const [sortField, setSortField] = useState<SortField>(initialField)
  const [sortDirection, setSortDirection] = useState<SortDirection>(initialDirection)
  const [sortingField, setSortingField] = useState<SortField>(null)

  // Toggle sort direction and field with sorting indicator
  const handleSort = useCallback((field: SortField) => {
    // Set the sorting indicator
    setSortingField(field)
    
    // Add a small delay to make the spinner visible
    // In reality, most sorts happen too quickly to see any spinner without this
    setTimeout(() => {
      if (field === sortField) {
        // Toggle direction if same field
        setSortDirection(prevDirection => prevDirection === 'asc' ? 'desc' : 'asc')
      } else {
        // Set new field and default to ascending for name and descending for reports (most recent first)
        setSortField(field)
        setSortDirection(field === 'reports' ? 'desc' : 'asc')
      }
      
      // Clear the sorting indicator after a delay to allow rendering to complete
      setTimeout(() => {
        setSortingField(null)
      }, SORT_DELAYS.SPINNER_VISIBILITY)
    }, SORT_DELAYS.SORTING_OPERATION)
  }, [sortField])

  return {
    sortField,
    sortDirection,
    sortingField,
    handleSort
  }
}

export default useSortableTable